import * as React from "react"
import { X } from "lucide-react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/core/utils"

const closeButtonVariants = cva(
  "inline-flex items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "text-muted-foreground hover:text-foreground hover:bg-muted",
        destructive: "text-red-600 hover:text-red-700 hover:bg-red-100 dark:text-red-500 dark:hover:text-red-400 dark:hover:bg-red-950",
        subtle: "opacity-60 hover:opacity-100 hover:bg-muted/50",
        ghost: "text-muted-foreground hover:text-foreground",
      },
      size: {
        default: "h-6 w-6",
        sm: "h-5 w-5",
        lg: "h-8 w-8",
        icon: "h-4 w-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const closeIconSizes = {
  default: "h-3.5 w-3.5",
  sm: "h-3 w-3",
  lg: "h-4 w-4",
  icon: "h-3 w-3",
}

export interface CloseButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof closeButtonVariants> {
  srText?: string
}

const CloseButton = React.forwardRef<HTMLButtonElement, CloseButtonProps>(
  ({ className, variant, size, srText = "Close", ...props }, ref) => {
    const iconSize = closeIconSizes[size || "default"]

    return (
      <button
        type="button"
        className={cn(closeButtonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        <X className={iconSize} />
        <span className="sr-only">{srText}</span>
      </button>
    )
  }
)
CloseButton.displayName = "CloseButton"

export { CloseButton, closeButtonVariants }
